import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import { Box, Typography } from "@mui/material";
import { INDIGO, SLATE } from "../constants/Colors";

const LANG_EXTENSIONS = {
  python,
  json,
};

const LANG_LABEL = {
  python: "Python",
  json: "JSON",
};

const neutralTheme = EditorView.theme({
  ".cm-activeLine": { backgroundColor: "transparent !important" },
  ".cm-activeLineGutter": { backgroundColor: "transparent !important" },
  ".cm-gutters": {
    backgroundColor: "#ffffff",
    borderRight: `1px solid ${SLATE[200]}`,
    color: SLATE[300],
  },
  ".cm-content": { backgroundColor: "#ffffff" },
  ".cm-scroller": { backgroundColor: "#ffffff" },
  "&": { backgroundColor: "#ffffff" },
  ".cm-selectionBackground": { backgroundColor: `${INDIGO[100]} !important` },
  "&.cm-focused .cm-selectionBackground": { backgroundColor: `${INDIGO[100]} !important` },
  ".cm-cursor": { borderLeftColor: SLATE[700] },
});

/**
 * Generic code editor with syntax highlighting and line numbers.
 *
 * Props:
 *   value      – string (source code)
 *   onChange   – called with updated string
 *   language   – "python" | "json" (default "python")
 *   height     – editor height (default "180px")
 *   placeholder – placeholder string
 */
export default function CodeEditor({
  value,
  onChange,
  language = "python",
  height = "180px",
  placeholder,
}) {
  const extensions = useMemo(() => {
    const langFn = LANG_EXTENSIONS[language] || python;
    return [langFn(), neutralTheme];
  }, [language]);

  return (
    <Box
      sx={{
        border: `1px solid ${SLATE[300]}`,
        borderRadius: "8px",
        overflow: "hidden",
        transition: "border-color 0.15s",
        "&:focus-within": { borderColor: INDIGO[500] },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: "12px",
          py: "5px",
          bgcolor: SLATE[50],
          borderBottom: `1px solid ${SLATE[200]}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: SLATE[400],
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {LANG_LABEL[language] || language}
        </Typography>
      </Box>

      <CodeMirror
        value={value || ""}
        onChange={onChange}
        extensions={extensions}
        height={height}
        theme="light"
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          indentOnInput: true,
          highlightActiveLine: true,
          highlightSelectionMatches: false,
          dropCursor: false,
          allowMultipleSelections: false,
        }}
        style={{ fontSize: "13px" }}
        placeholder={placeholder}
      />
    </Box>
  );
}
